import React, { useMemo, useState } from "react";
import {
  Combobox,
  useCombobox,
  Input,
  SimpleGrid,
  Box,
  Flex,
  ScrollArea,
} from "@mantine/core";
import styles from "@scss/custom-components/form-fields-dropdowns.module.scss";

type Variant = "default" | "dark" | "darker" | "sky";

export type IconOption = {
  value: string;
  label: string;
  icon: React.ReactNode | string;
};

type IconPickerProps = {
  value?: string | null;
  onChange?: (v: string) => void;
  placeholder?: string;
  label?: string;
  cols?: number;
  options?: IconOption[] | undefined;
  disabled?: boolean;
  variant?: Variant;
  classNames?: Record<string, string>;
  showLabelInInput?: boolean;
  name?: string;
};

export function CustomIconSelect({
  value,
  onChange,
  placeholder = "Select icon",
  cols = 4,
  options = [],
  disabled,
  variant = "default",
  classNames = {},
  showLabelInInput = true,
  name,
}: IconPickerProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Fallback for uncontrolled usage
  const [innerValue, setInnerValue] = useState<string | null>(null);
  const effectiveValue = value ?? innerValue;

  const selected = useMemo(
    () => options.filter(Boolean).find((o) => o.value === effectiveValue) ?? null,
    [options, effectiveValue],
  );

  const variantDropdownClasses: Record<Variant, string> = {
    default: styles["dropdown--default"],
    dark: styles["dropdown--dark"],
    darker: styles["dropdown--darker"],
    sky: styles["dropdown--sky"],
  };

  return (
    <Combobox
      store={combobox}
      withinPortal
      classNames={{
        ...classNames,
        dropdown: `${variantDropdownClasses[variant]} ${classNames.dropdown ?? ""}`,
        options: `${styles.options} ${classNames.options ?? ""}`,
        option: `${styles.option} ${classNames.option ?? ""}`,
      }}
      onOptionSubmit={(val) => {
        setInnerValue(val);
        if (onChange) onChange(val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <Input
          variant={variant}
          readOnly
          disabled={disabled}
          onClick={() => combobox.toggleDropdown()}
          value={showLabelInInput && selected ? selected.label : ""}
          placeholder={placeholder}
          leftSection={selected ? selected.icon : undefined}
          rightSection={<></>}
          rightSectionPointerEvents="none"
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options className={styles.options}>
          <ScrollArea.Autosize
            mah={280}
            type="auto"
            classNames={{
              scrollbar: styles.sbScrollbar,
              thumb: styles.sbThumb,
              corner: styles.sbCorner,
              viewport: styles.sbViewport,
            }}
          >
            <Box p="xs">
              <SimpleGrid
                cols={cols}
                mr={8}
                verticalSpacing={0}
                className={styles.iconGrid}
              >
                {options.map((opt) => (
                  <Combobox.Option
                    key={opt.value}
                    value={opt.value}
                    aria-label={opt.label}
                    title={opt.label}
                    className={styles.iconBox}
                  >
                    <Flex
                      data-selected={opt.value === effectiveValue || undefined}
                    >
                      <Box>{opt.icon}</Box>
                    </Flex>
                  </Combobox.Option>
                ))}
              </SimpleGrid>
            </Box>
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>

      {name && <input type="hidden" name={name} value={effectiveValue ?? ""} />}
    </Combobox>
  );
}
